import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@shared/components/ui/button";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import { Switch } from "@shared/components/ui/switch";
import { useTranslation } from "react-i18next";
import { Upload, User } from "lucide-react";
import { useAuth } from "@features/auth/contexts/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { SelectUser } from "@/features/users/SelectUser";

export function ProductForm({
  initialData,
  action,
  onSubmit,
  isLoading = false,
}) {
  const { t } = useTranslation();
  const { isAdmin, isManager } = useAuth();
  const [newImagePreview, setNewImagePreview] = useState(null);

  const formSchema = z.object({
    name: z.string().min(2, {
      message: t("products.validation.required", {
        field: t("products.form.name"),
      }),
    }),
    price: z.number().positive({
      message: t("pricePositive"),
    }),
    isActive: z.boolean(),
    allowOpening: z.boolean(),
    allowFitting: z.boolean(),
    allowExchange: z.boolean(),
    image: z.any().optional().nullable(),
    user:
      isAdmin || isManager
        ? z.string().min(1, { message: t("users.selectClientRequired") })
        : z.string().optional(),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: initialData || {
      name: "",
      price: 0,
      isActive: true,
      allowOpening: false,
      allowFitting: false,
      allowExchange: true,
      image: undefined,
      user: "",
    },
  });

  function handleSubmit(values) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(values)) {
      if (key === "image" && values.image?.[0]) {
        formData.append(key, values.image[0]);
      } else {
        formData.append(key, value?.toString());
      }
    }

    onSubmit(formData);
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setNewImagePreview(null);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
        {/* Client Selection - Admin/Manager Only */}
        {(isAdmin || isManager) && (
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-500" />
                {t("users.selectClient")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="user"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("users.selectClient")} *</FormLabel>
                    <FormControl>
                      <SelectUser
                        onSelectUser={field.onChange}
                        user={field.value}
                        role="client"
                        showDetails={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col md:flex-row items-center gap-6 mb-5">
          <FormField
            control={form.control}
            name="image"
            render={({ field: { value, onChange, ...field } }) => (
              <FormItem>
                <FormControl>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center h-40 w-40">
                      <label
                        htmlFor="dropzone-file"
                        className="flex flex-col w-full h-full items-center justify-center border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600 relative overflow-hidden"
                      >
                        {newImagePreview || value ? (
                          <div className="absolute inset-0 w-full h-full group">
                            <img
                              src={
                                newImagePreview ||
                                `http://localhost:8000/public/products/${value}`
                              }
                              alt="New product image preview"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <Upload className="w-12 h-12 text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center px-2">
                            <Upload className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" />
                            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400 ">
                              <span className="font-semibold">
                                Click to upload
                              </span>{" "}
                              or drag and drop
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {/* SVG, PNG, JPG or GIF (MAX. 800x400px) */}
                            </p>
                          </div>
                        )}
                        <Input
                          id="dropzone-file"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            onChange(e.target.files);
                            handleImageChange(e);
                          }}
                          name={field.name}
                          ref={field.ref}
                        />
                      </label>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="w-full space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("products.form.name")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("products.form.name")}
                      {...field}
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("products.form.price")} / {t("main.currency")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="20"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value))
                      }
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {t("products.form.isActive")}
                  </FormLabel>
                  <FormDescription>
                    {t("products.form.isProductActive")}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="allowOpening"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {t("products.form.allowOpening")}
                  </FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="allowFitting"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {t("products.form.allowFitting")}
                  </FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="allowExchange"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {t("products.form.allowExchange")}
                  </FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {action === "create"
            ? t("products.form.createProduct")
            : t("products.form.updateProduct")}
        </Button>
      </form>
    </Form>
  );
}
